import {useMemo} from 'react';
import {getDateFormat, getHourMinutes} from '../../../library/dates';
import {TSlot, TTimetableItem} from '../../../redux/timetable/timetable.type';

const formatTime = (timestamp: number) => getHourMinutes(timestamp);
const getDayKey = (timestamp: number) => getDateFormat(timestamp, 'YYYY-M-D');

// The slot's timeStart is the authoritative date for a lecture — l.time can be
// stale (e.g. after duplicate, only tsid is persisted, time stays 0).
export const getLectureTime = (l: TTimetableItem, slots: TSlot[]): number =>
    slots.find(s => s.tsid === l.tsid)?.timeStart ?? l.time;

type ConferenceDay = {key: string; label: string; timestamp: number};

export function useTimeGroups(lectures: TTimetableItem[], currentDay: ConferenceDay | undefined, slots: TSlot[]) {
    return useMemo(() => {
        const filtered = currentDay
            ? lectures.filter(l => getDayKey(getLectureTime(l, slots)) === currentDay.key)
            : lectures;

        const lectureItems: TTimetableItem[] = [];
        const breakItems: TTimetableItem[] = [];
        for(const item of filtered) {
            (item.isBreak ? breakItems : lectureItems).push(item);
        }

        const groupsMap = new Map<string, {time: string; rawTime: number; tsid: number | null; lectures: TTimetableItem[]}>();
        for(const lecture of lectureItems) {
            const groupKey =  `s:${lecture.tsid}` ;
            const lectureTime = getLectureTime(lecture, slots);
            const group = groupsMap.get(groupKey) ?? {time: formatTime(lectureTime), rawTime: lectureTime, tsid: lecture.tsid ?? null, lectures: []};
            group.lectures.push(lecture);
            groupsMap.set(groupKey, group);
        }
        const grouped = Array.from(groupsMap.values());

        const breakGroups = breakItems.map(b => {
            const breakTime = getLectureTime(b, slots);
            return {
                time: formatTime(breakTime),
                rawTime: breakTime,
                isBreak: true as const,
                breakItem: b,
            };
        });

        return [...grouped, ...breakGroups].sort((a, b) => a.rawTime - b.rawTime);
    }, [lectures, currentDay, slots]);
}
